import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, forkJoin, Observable, of, switchMap, tap } from 'rxjs';
import { PatientService } from './patient.service';
import { SocketService } from './socket.service';

@Injectable({
  providedIn: 'root'
})
export class PatientPrescriptionService {
  private apiUrl = `api`;

  private patientPrescriptionsSubject = new BehaviorSubject<any[] | null>(null);
  private patientMedicationSubject = new BehaviorSubject<any[] | null>(null);
  private cloningMedicationSubject = new BehaviorSubject<any>(null);

  patientPrescriptions$ = this.patientPrescriptionsSubject.asObservable();
  patientMedication$ = this.patientMedicationSubject.asObservable();
  cloningMedication$ = this.cloningMedicationSubject.asObservable();

  constructor(
    private http: HttpClient,
    private patientService: PatientService,
    private socketService: SocketService
  ) {
    this.subscribeToCurrentPatient();
    this.setupSocketListeners();
  }

  private subscribeToCurrentPatient(): void {
    this.patientService.currentPatient$.pipe(
      switchMap(patient => {
        if (patient) {
          this.patientPrescriptionsSubject.next([]);
          this.patientMedicationSubject.next([]);
          return forkJoin([
            this.loadPrescriptionsByPatient(patient.id),
            this.loadMedicationByPatient(patient.id)
          ]);
        } else {
          this.patientPrescriptionsSubject.next([]);
          this.patientMedicationSubject.next([]);
          return of<[any[], any[]]>([[], []]); // Return an observable with typed empty arrays
        }
      }),
      tap(([prescriptions, medication]: [any, any]) => {
        // Update the BehaviorSubject with the new or cleared notes
        this.patientPrescriptionsSubject.next(prescriptions?.data || []);
        this.patientMedicationSubject.next(medication?.data || []);
      })
    ).subscribe();
  }



  private setupSocketListeners(): void {

    // Current patient's ID
    let currentPatientId: string | null = null;

    // Subscribe to the current patient changes to keep track of the current patient's ID
    this.patientService.currentPatient$.subscribe(patient => {
      currentPatientId = patient ? patient.id : null;
    });

    this.socketService.listen('prescriptionAdded', 'patient-prescription-service').subscribe(item => {
      if (item.patientId === currentPatientId) {
        const current = this.patientPrescriptionsSubject.getValue() || [];
        current.unshift(item);
        this.patientPrescriptionsSubject.next(current);
      }
    });


    this.socketService.listen('medicationAdded', 'patient-prescription-service').subscribe(item => {
      if (item.patientId === currentPatientId) {
        const current = this.patientMedicationSubject.getValue() || [];
        current.unshift(item);
        this.patientMedicationSubject.next(current);
      }
    });

    this.socketService.listen('medicationUpdated', 'patient-prescription-service').subscribe(item => {
      if (item.patientId === currentPatientId) {
        const current = this.patientMedicationSubject.getValue() || [];
        const updated = item.deleted ? current.filter(i => i.id !== item.id) : current!.map(i => i.id === item.id ? item : i);
        this.patientMedicationSubject.next(updated);
      }
    });

  }

  loadPrescriptionsByPatient(patientId: string): Observable<any[]> {
    const token = localStorage.getItem('token');
    return this.http.get<any[]>(`${this.apiUrl}/prescriptions/patient/${patientId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  loadMedicationByPatient(patientId: string): Observable<any[]> {
    const token = localStorage.getItem('token');
    return this.http.get<any[]>(`${this.apiUrl}/medications/patient/${patientId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  updateMedication(id: string, medication: any): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.put<any>(`${this.apiUrl}/medications/patient/${id}`, medication, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  setCloning(medication: any | null) {
    this.cloningMedicationSubject.next(medication);
  }


}
